/**
 * A page's detection settings, behind the "Configurações" button in its header: the extraction
 * switches that page's `kind` cares about, plus the auto-accept threshold on Posts and
 * Comentários. The API holds one `OntologySettings` per organisation, not one per page, so a save
 * PUTs the whole object -- a partial write would reset every field this page does not show -- and
 * the threshold the two content pages share says so.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Settings } from 'lucide-react';
import { toast } from 'sonner';

import { FormDialog } from '../../../chrome/form';
import { Button } from '../../../ui/button';
import { Field, FieldError, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Slider } from '../../../ui/slider';
import { Switch } from '../../../ui/switch';
import { useUpdateSettings } from './api/queries';
import type { AutoAcceptDefaults, OntologyPageKind, OntologySettings } from './api/schemas';
import { om } from './messages';

/** Perfis never shows the auto-accept threshold: a profile value always waits for an
 *  administrator (`DimensionList.tsx`'s own note), so there is no bar to configure. */
const FLAGS_BY_KIND: Record<OntologyPageKind, (keyof OntologySettings)[]> = {
  posts: ['ner_posts'],
  comments: ['ner_comments', 'profile_in_comments'],
  profiles: ['profile_recent_posts'],
};

/** `null` keeps the platform default; anything else must be a whole number of at least 1, the
 *  API's own floor. */
function occurrencesOf(text: string): number | null | 'invalid' {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  const value = Number(trimmed);
  return value >= 1 ? value : 'invalid';
}

function percent(confidence: number): number {
  return Math.round(confidence * 100);
}

export function DetectionSettings(props: {
  tenantId: string;
  kind: OntologyPageKind;
  settings: OntologySettings;
  defaults: AutoAcceptDefaults;
}): ReactNode {
  const { settings, defaults, kind } = props;
  const flags = FLAGS_BY_KIND[kind];
  const showAutoAccept = kind !== 'profiles';
  const updateMutation = useUpdateSettings(props.tenantId);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(settings);
  const [occurrencesText, setOccurrencesText] = useState('');
  const [occurrencesError, setOccurrencesError] = useState<string | null>(null);

  // Seeded from the latest `settings` each time the dialog opens, so an edit abandoned with
  // Cancel never survives into the next visit.
  function handleOpenChange(next: boolean): void {
    if (next) {
      setDraft(settings);
      setOccurrencesText(settings.auto_accept_occurrences?.toString() ?? '');
      setOccurrencesError(null);
      updateMutation.reset();
    }
    setOpen(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void | Promise<unknown> {
    event.preventDefault();
    const occurrences = occurrencesOf(occurrencesText);
    if (occurrences === 'invalid') {
      setOccurrencesError(om('flags.autoAccept.occurrences.invalid'));
      return;
    }
    setOccurrencesError(null);
    return updateMutation
      .mutateAsync({ ...draft, auto_accept_occurrences: occurrences })
      .then(() => toast.success(om('flags.saved')));
  }

  const confidence = percent(draft.auto_accept_confidence ?? defaults.confidence);

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <Button type="button" variant="outline" size="sm">
          <Settings aria-hidden />
          {om('settings.open')}
        </Button>
      }
      title={om('flags.heading')}
      submitLabel={om('flags.save')}
      pending={updateMutation.isPending}
      failure={updateMutation.error?.failure ?? null}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-3">
        {flags.map((flag) => (
          <label key={flag} className="flex items-center justify-between gap-4 text-body-sm">
            <span className="text-foreground">
              {om(`flags.${flag}` as Parameters<typeof om>[0])}
            </span>
            <Switch
              aria-label={om(`flags.${flag}` as Parameters<typeof om>[0])}
              checked={draft[flag] as boolean}
              onCheckedChange={(checked) => setDraft({ ...draft, [flag]: checked })}
            />
          </label>
        ))}
      </div>

      {showAutoAccept && (
        <div className="flex flex-col gap-4 border-t pt-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-body-sm font-medium text-foreground">
              {om('flags.autoAccept.heading')}
            </h3>
            <p className="text-caption text-muted-foreground">{om('flags.autoAccept.shared')}</p>
          </div>
          <Field>
            <div className="flex items-center justify-between gap-4">
              <FieldLabel>{om('flags.autoAccept.confidence.label')}</FieldLabel>
              <span data-numeric className="text-body-sm text-foreground">
                {om('flags.autoAccept.confidence.value', { value: confidence })}
              </span>
            </div>
            <Slider
              aria-label={om('flags.autoAccept.confidence.label')}
              getAriaValueText={(value) => om('flags.autoAccept.confidence.value', { value })}
              min={0}
              max={100}
              step={1}
              value={[confidence]}
              onValueChange={([value]) =>
                setDraft({ ...draft, auto_accept_confidence: (value ?? confidence) / 100 })
              }
            />
          </Field>
          <Field data-invalid={occurrencesError !== null || undefined}>
            <FieldLabel htmlFor={`${props.kind}-occurrences`}>
              {om('flags.autoAccept.occurrences.label')}
            </FieldLabel>
            <Input
              id={`${props.kind}-occurrences`}
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              placeholder={defaults.occurrences.toString()}
              value={occurrencesText}
              onChange={(event) => setOccurrencesText(event.target.value)}
              aria-invalid={occurrencesError !== null || undefined}
            />
            <FieldError>{occurrencesError}</FieldError>
          </Field>
        </div>
      )}
    </FormDialog>
  );
}
